import {
  DashboardStatisticsModel,
  DashboardYearWiseStatisticsModel,
  CountryDataModel
} from "../models/DashboardStatisticsModel";
import {
  WorldMapAssessmentStatusCountries,
  WorldMapAssessmentApproachCountries
} from "../models/Enums";

const ISO_CODES = {
  ANGOLA: "AGO",
  BENIN: "BEN",
  BOTSWANA: "BWA",
  BURKINA_FASO: "BFA",
  BURUNDI: "BDI",
  CAMBODIA: "KHM",
  CAMEROON: "CMR",
  DRC: "COD",
  ESWATINI: "SWZ",
  ETHIOPIA: "ETH",
  GHANA: "GHA",
  INDONESIA: "IDN",
  LAOS: "LAO",
  SAO_TOME: "STP",
  SIERRA_LEONE: "SLE",
  TANZANIA: "TZA",
  GAMBIA: "GMB",
  TOGO: "TGO",
  UGANDA: "UGA",
  VIETNAM: "VNM",
  NIGERIA: "NGA",
  MOZAMBIQUE: "MOZ",
  NAMIBIA: "NAM",
  SOUTH_AFRICA: "ZAF",
  ZAMBIA: "ZMB",
  ZIMBABWE: "ZWE",
  PAKISTAN: "PAK",
  GUINEA: "GIN",
};

export interface SurveillanceAssessment {
  iso: string;
  country: string;
  region: string;
  status: "Complete" | "Ongoing" | "Planned";
  year: number;
  type: "Rapid" | "Tailored" | "Comprehensive";
}

export const surveillanceAssessmentsData: SurveillanceAssessment[] = [
  { iso: ISO_CODES.ANGOLA, country: "Angola", region: "AFRO", status: "Complete", year: 2022, type: "Rapid" },
  { iso: ISO_CODES.BENIN, country: "Benin", region: "AFRO", status: "Complete", year: 2021, type: "Comprehensive" },
  { iso: ISO_CODES.BOTSWANA, country: "Botswana", region: "AFRO", status: "Complete", year: 2024, type: "Rapid" },
  { iso: ISO_CODES.BURKINA_FASO, country: "Burkina Faso", region: "AFRO", status: "Complete", year: 2020, type: "Comprehensive" },
  { iso: ISO_CODES.CAMEROON, country: "Cameroon", region: "AFRO", status: "Complete", year: 2021, type: "Tailored" },
  { iso: ISO_CODES.DRC, country: "DRC", region: "AFRO", status: "Complete", year: 2020, type: "Comprehensive" },
  { iso: ISO_CODES.ESWATINI, country: "Eswatini", region: "AFRO", status: "Complete", year: 2022, type: "Tailored" },
  { iso: ISO_CODES.GHANA, country: "Ghana", region: "AFRO", status: "Complete", year: 2021, type: "Comprehensive" },
  { iso: ISO_CODES.SAO_TOME, country: "Sao Tome", region: "AFRO", status: "Complete", year: 2024, type: "Rapid" },
  { iso: ISO_CODES.GAMBIA, country: "The Gambia", region: "AFRO", status: "Complete", year: 2024, type: "Rapid" },
  { iso: ISO_CODES.NIGERIA, country: "Nigeria", region: "AFRO", status: "Complete", year: 2022, type: "Tailored" },
  { iso: ISO_CODES.MOZAMBIQUE, country: "Mozambique", region: "AFRO", status: "Complete", year: 2022, type: "Tailored" },
  { iso: ISO_CODES.NAMIBIA, country: "Namibia", region: "AFRO", status: "Complete", year: 2022, type: "Tailored" },
  { iso: ISO_CODES.SOUTH_AFRICA, country: "South Africa", region: "AFRO", status: "Complete", year: 2022, type: "Tailored" },
  { iso: ISO_CODES.ZAMBIA, country: "Zambia", region: "AFRO", status: "Complete", year: 2022, type: "Tailored" },
  { iso: ISO_CODES.ZIMBABWE, country: "Zimbabwe", region: "AFRO", status: "Complete", year: 2022, type: "Tailored" },
  { iso: ISO_CODES.BURUNDI, country: "Burundi", region: "AFRO", status: "Ongoing", year: 2025, type: "Tailored" },
  { iso: ISO_CODES.ETHIOPIA, country: "Ethiopia", region: "AFRO", status: "Ongoing", year: 2024, type: "Comprehensive" },
  { iso: ISO_CODES.SIERRA_LEONE, country: "Sierra Leone", region: "AFRO", status: "Ongoing", year: 2025, type: "Tailored" },
  { iso: ISO_CODES.TANZANIA, country: "Tanzania", region: "AFRO", status: "Ongoing", year: 2025, type: "Tailored" },
  { iso: ISO_CODES.UGANDA, country: "Uganda", region: "AFRO", status: "Ongoing", year: 2025, type: "Tailored" },
  { iso: ISO_CODES.TANZANIA, country: "Tanzania", region: "AFRO", status: "Ongoing", year: 2026, type: "Rapid" },
  { iso: ISO_CODES.GUINEA, country: "Guinea", region: "AFRO", status: "Ongoing", year: 2026, type: "Rapid" },
  { iso: ISO_CODES.CAMBODIA, country: "Cambodia", region: "SEARO", status: "Complete", year: 2023, type: "Tailored" },
  { iso: ISO_CODES.INDONESIA, country: "Indonesia", region: "SEARO", status: "Complete", year: 2022, type: "Rapid" },
  { iso: ISO_CODES.LAOS, country: "Laos PDR", region: "SEARO", status: "Complete", year: 2023, type: "Tailored" },
  { iso: ISO_CODES.VIETNAM, country: "Vietnam", region: "SEARO", status: "Complete", year: 2024, type: "Tailored" },
  { iso: ISO_CODES.PAKISTAN, country: "Pakistan", region: "EMRO", status: "Ongoing", year: 2026, type: "Rapid" },
];

const dedupByIsoLatestYear = (
  assessments: SurveillanceAssessment[]
): SurveillanceAssessment[] => {
  const latestByIso = new Map<string, SurveillanceAssessment>();
  for (const a of assessments) {
    const existing = latestByIso.get(a.iso);
    if (!existing || a.year > existing.year) {
      latestByIso.set(a.iso, a);
    }
  }
  return Array.from(latestByIso.values());
};

export const getStaticDashboardStatistics = (): DashboardStatisticsModel => {
  const completedAssessments = surveillanceAssessmentsData.filter(
    a => a.status === "Complete"
  );
  const ongoingAssessments = surveillanceAssessmentsData.filter(
    a => a.status === "Ongoing" || a.status === "Planned"
  );

  const completedCountByIso = new Map<string, number>();
  for (const a of completedAssessments) {
    completedCountByIso.set(a.iso, (completedCountByIso.get(a.iso) ?? 0) + 1);
  }

  const completedLatestByIso = dedupByIsoLatestYear(completedAssessments);
  const singleCompleted = completedLatestByIso.filter(
    a => (completedCountByIso.get(a.iso) ?? 0) === 1
  );
  const multipleCompleted = completedLatestByIso.filter(
    a => (completedCountByIso.get(a.iso) ?? 0) > 1
  );

  const ongoingLatestByIso = dedupByIsoLatestYear(ongoingAssessments).filter(
    a => !completedCountByIso.has(a.iso)
  );

  const rapidLatest = surveillanceAssessmentsData.filter(a => a.type === "Rapid");
  const tailoredLatest = surveillanceAssessmentsData.filter(a => a.type === "Tailored");
  const comprehensiveLatest = surveillanceAssessmentsData.filter(a => a.type === "Comprehensive");

  const inProgressSurveillanceAssessments: CountryDataModel[] = ongoingLatestByIso.map(
    a => new CountryDataModel(a.iso, WorldMapAssessmentStatusCountries.InprogessAssessment)
  );

  const completedSurveillanceAssessments: CountryDataModel[] = singleCompleted.map(
    a => new CountryDataModel(a.iso, WorldMapAssessmentStatusCountries.CompletedOneAssessment)
  );

  const multipleCompletedSurveillanceAssessments: CountryDataModel[] = multipleCompleted.map(
    a => new CountryDataModel(a.iso, WorldMapAssessmentStatusCountries.MoreThanOneAssessment)
  );

  const rapidSurveillanceAssessments: CountryDataModel[] = rapidLatest.map(
    a => new CountryDataModel(a.iso, WorldMapAssessmentApproachCountries.RapidAssessment)
  );

  const tailoredSurveillanceAssessments: CountryDataModel[] = tailoredLatest.map(
    a => new CountryDataModel(a.iso, WorldMapAssessmentApproachCountries.TailoredAssessment)
  );

  const comprehensiveSurveillanceAssessments: CountryDataModel[] = comprehensiveLatest.map(
    a => new CountryDataModel(a.iso, WorldMapAssessmentApproachCountries.ComprehensiveAssessment)
  );

  return new DashboardStatisticsModel(
    inProgressSurveillanceAssessments,
    completedSurveillanceAssessments,
    multipleCompletedSurveillanceAssessments,
    rapidSurveillanceAssessments,
    tailoredSurveillanceAssessments,
    comprehensiveSurveillanceAssessments
  );
};

export const getStaticYearWiseStatistics = (year: string): DashboardYearWiseStatisticsModel => {
  const yearNum = parseInt(year, 10);

  const yearAssessments = surveillanceAssessmentsData.filter(a => a.year === yearNum);

  const inProgressForYear = yearAssessments.filter(
    a => a.status === "Ongoing" || a.status === "Planned"
  );
  const completedForYear = yearAssessments.filter(a => a.status === "Complete");

  const inProgressAssessments: CountryDataModel[] = inProgressForYear.map(
    a => new CountryDataModel(a.iso, WorldMapAssessmentStatusCountries.InprogessAssessment)
  );

  const completedAssessments: CountryDataModel[] = completedForYear.map(
    a => new CountryDataModel(a.iso, WorldMapAssessmentStatusCountries.MoreThanOneAssessment)
  );

  return new DashboardYearWiseStatisticsModel(inProgressAssessments, completedAssessments);
};

export const getStaticAvailableYears = (): number[] => {
  const years = [...new Set(surveillanceAssessmentsData.map(a => a.year))];
  return years.sort((a, b) => b - a);
};
